import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { collection, getDocs, getFirestore, limit, query } from 'firebase/firestore';
import { usePopup } from '../../contexts/PopupContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTourPartyMembers } from '../../hooks/useTourPartyMembers';
import { useInterestCatalog } from '../../hooks/useInterestCatalog';
import { interestTooltipLabel, splitInterestEmojiGlyphs, resolveInterestDisplayItems } from '../../utils/interestUtils';
import { INVITE_STATUS_META, memberDisplayName, normalizeEmail, resolveMemberLabel } from '../../utils/tourPartyMemberUtils';
import {
  canManageTourParty,
  isTourOwner,
  getMemberPermissionsEntry,
  normalizeMemberPermissionsEntry,
  saveMemberPermissionEntry,
  CALENDAR_PERMISSION_TABS,
  PERMISSION_LEVELS,
} from '../../utils/tourMemberPermissionsService';
import { createTourInvite, resendTourInvite, revokeTourInvite } from '../../utils/tourInviteService';
import { TourPartyMemberAvatar } from './TourPartyMemberAvatar';
import { CardInterestsDisplay } from '../CardInterestsDisplay';
import { MemberPermissionsEditor } from './TourPartyPermissionsPanel';

export const TOUR_PARTY_RAIL_WIDTH_COLLAPSED = 108;
export const TOUR_PARTY_RAIL_DETAIL_WIDTH = 360;
export const TOUR_PARTY_RAIL_WIDTH_EXPANDED =
  TOUR_PARTY_RAIL_WIDTH_COLLAPSED + TOUR_PARTY_RAIL_DETAIL_WIDTH;
/** @deprecated use TOUR_PARTY_RAIL_WIDTH_COLLAPSED */
export const TOUR_PARTY_RAIL_WIDTH = TOUR_PARTY_RAIL_WIDTH_COLLAPSED;

const CARD_BODY_WIDTH = 72;
const INTEREST_STRIP_WIDTH = 26;
const CARD_WIDTH = CARD_BODY_WIDTH + INTEREST_STRIP_WIDTH;
const DOOR_CARD_ANIM_MS = 280;
const DOOR_CARD_STAGGER_MS = 48;

function getDoorCardExitDuration(itemCount) {
  return DOOR_CARD_ANIM_MS + Math.max(0, itemCount - 1) * DOOR_CARD_STAGGER_MS;
}

/**
 * Staggered slide-down enter / slide-up exit when the active door date changes.
 * Uses a generation counter so rapid door changes cannot leave cards stuck invisible.
 */
function useDoorPartyCardMotion(doorDateKey, visible) {
  const itemCountRef = useRef(0);
  const [phase, setPhase] = useState('hidden');
  const genRef = useRef(0);
  const prevDoorRef = useRef(doorDateKey);
  const prevVisibleRef = useRef(visible);
  const didMountRef = useRef(false);

  useEffect(() => {
    const gen = ++genRef.current;
    const alive = () => genRef.current === gen;
    const timers = [];

    const schedule = (fn, ms) => {
      const id = setTimeout(() => {
        if (!alive()) return;
        fn();
      }, ms);
      timers.push(id);
    };

    const exitDuration = () => getDoorCardExitDuration(Math.max(itemCountRef.current, 1));

    const runEnter = () => {
      setPhase('entering');
      schedule(() => {
        if (!alive()) return;
        setPhase('shown');
      }, 34);
    };

    const runExit = (onDone) => {
      setPhase('exiting');
      schedule(() => {
        if (!alive()) return;
        onDone();
      }, exitDuration());
    };

    const prevDoor = prevDoorRef.current;
    const prevVisible = prevVisibleRef.current;
    prevDoorRef.current = doorDateKey;
    prevVisibleRef.current = visible;

    if (!didMountRef.current) {
      didMountRef.current = true;
      if (visible) runEnter();
      else setPhase('hidden');
    } else if (!visible) {
      if (prevVisible) {
        runExit(() => {
          if (!alive()) return;
          setPhase('hidden');
        });
      } else {
        setPhase('hidden');
      }
    } else if (!prevVisible) {
      runEnter();
    } else if (doorDateKey !== prevDoor) {
      runExit(runEnter);
    }

    // If enter was interrupted (e.g. member count changed mid-animation), recover quickly.
    if (visible) {
      schedule(() => {
        if (!alive()) return;
        setPhase((current) =>
          current === 'entering' || current === 'hidden' ? 'shown' : current
        );
      }, 120);
    }

    return () => {
      genRef.current += 1;
      timers.forEach(clearTimeout);
    };
  }, [doorDateKey, visible]);

  const showCards = visible || phase === 'exiting';

  return { phase, showCards, itemCountRef };
}

function getDoorCardMotionStyle(index, phase, railVisible) {
  const delay = index * DOOR_CARD_STAGGER_MS;
  const isEntering = phase === 'entering';
  const showCard =
    phase === 'shown' ||
    (railVisible && phase === 'hidden'); // recovery if animation state gets stuck

  if (Platform.OS === 'web') {
    return {
      opacity: showCard ? 1 : 0,
      transform: showCard ? 'translateY(0) scale(1)' : 'translateY(-26px) scale(0.95)',
      transition: isEntering
        ? 'none'
        : `transform ${DOOR_CARD_ANIM_MS}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, opacity ${DOOR_CARD_ANIM_MS}ms ease ${delay}ms`,
    };
  }

  return showCard
    ? { opacity: 1, transform: [{ translateY: 0 }, { scale: 1 }] }
    : { opacity: 0, transform: [{ translateY: -26 }, { scale: 0.95 }] };
}

function DoorPartyAnimatedCard({ index, phase, railVisible, style, children }) {
  return (
    <View style={[styles.doorCardMotionWrap, getDoorCardMotionStyle(index, phase, railVisible), style]}>
      {children}
    </View>
  );
}

function DoorPartyAnimatedAddButton({ index, phase, railVisible, theme, onPress }) {
  return (
    <DoorPartyAnimatedCard index={index} phase={phase} railVisible={railVisible}>
      <TouchableOpacity
        style={[styles.addCardBtn, { borderColor: theme.brand.primary }]}
        onPress={onPress}
        accessibilityLabel="Add tour member"
      >
        <Ionicons name="add" size={22} color={theme.brand.primary} />
      </TouchableOpacity>
    </DoorPartyAnimatedCard>
  );
}

function memberKey(member) {
  return String(member.id || member.email || member.name);
}

function canOpenProfile(member) {
  const id = String(member?.id || '').trim();
  return Boolean(member?.onPlatform && id && !id.includes('@'));
}

function VerticalInterestStrip({ items, theme }) {
  const maxVisible = 4;

  if (!items.length) {
    return (
      <View style={[styles.interestStrip, { borderLeftColor: `${theme.ui.border}aa` }]}>
        <Text style={[styles.interestEmpty, { color: theme.text.tertiary }]}>—</Text>
      </View>
    );
  }

  const visible = items.slice(0, maxVisible);
  const overflow = Math.max(0, items.length - visible.length);

  return (
    <View style={[styles.interestStrip, { borderLeftColor: `${theme.ui.border}aa` }]}>
      {visible.map((item, idx) => {
        const tooltip = interestTooltipLabel(item.label);
        const glyphs = splitInterestEmojiGlyphs(item.emoji);
        if (Platform.OS === 'web') {
          return (
            <span key={item.id || idx} title={tooltip} aria-label={tooltip} style={webInterestEmojiStyle}>
              {glyphs.join('')}
            </span>
          );
        }
        return (
          <Text key={item.id || idx} style={styles.interestEmoji} accessibilityLabel={tooltip}>
            {glyphs.join('')}
          </Text>
        );
      })}
      {overflow > 0 ? (
        <Text style={[styles.interestOverflow, { color: theme.text.secondary }]}>+{overflow}</Text>
      ) : null}
    </View>
  );
}

function TourPartyRailCard({
  member,
  theme,
  selected,
  onPress,
  interestItems,
  canRemove = false,
  onRemove,
}) {
  return (
    <View
      style={[
        styles.railCard,
        {
          width: CARD_WIDTH,
          maxWidth: CARD_WIDTH,
          minWidth: CARD_WIDTH,
          borderColor: selected ? theme.brand.primary : theme.ui.border,
          backgroundColor: theme.background.secondary,
          borderWidth: selected ? 2 : 1,
        },
      ]}
    >
      {canRemove ? (
        <TouchableOpacity
          style={[styles.railCardRemoveBtn, { backgroundColor: theme.background.elevated, borderColor: theme.ui.border }]}
          onPress={onRemove}
          hitSlop={6}
          accessibilityLabel={`Remove ${member.name || 'member'}`}
        >
          <Ionicons name="close" size={11} color={theme.status.error} />
        </TouchableOpacity>
      ) : null}
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.88}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        accessibilityLabel={`${member.name}, tour party member`}
        style={styles.railCardPressable}
      >
        <View style={styles.railCardBody}>
          <TourPartyMemberAvatar member={member} size={32} theme={theme} />
        <Text style={[styles.railCardName, { color: theme.text.primary }]} numberOfLines={1}>
          {resolveMemberLabel(member).split(' ')[0] || resolveMemberLabel(member)}
        </Text>
          {member.userTypeLabel ? (
            <Text
              style={[styles.railCardType, { color: member.userTypeColor || theme.text.secondary }]}
              numberOfLines={1}
            >
              {member.userTypeLabel}
            </Text>
          ) : null}
        </View>
        <VerticalInterestStrip items={interestItems} theme={theme} />
      </TouchableOpacity>
    </View>
  );
}

function TourPartyRailAddPanel({
  theme,
  ownerId,
  ownerName,
  ownerEmail,
  tourId,
  tourName,
  invitedEmails,
  onSuccess,
  onError,
  onClose,
}) {
  const db = getFirestore();
  const { showConfirmation } = usePopup();
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const runSearch = useCallback(
    async (term) => {
      setSearch(term);
      if (term.trim().length < 2) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      try {
        const snap = await getDocs(query(collection(db, 'users'), limit(150)));
        const lower = term.toLowerCase();
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => u.id !== ownerId)
          .filter((u) => normalizeEmail(u.email))
          .filter((u) => !invitedEmails.has(normalizeEmail(u.email)))
          .filter((u) => {
            const hay = [u.firstName, u.lastName, u.username, u.email, `${u.firstName || ''} ${u.lastName || ''}`]
              .join(' ')
              .toLowerCase();
            return hay.includes(lower);
          })
          .slice(0, 6);
        setSearchResults(rows);
      } catch (err) {
        console.warn('TourPartyRail search', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [db, ownerId, invitedEmails]
  );

  const sendInvite = useCallback(
    async ({ email, name }) => {
      const emailLower = normalizeEmail(email);
      if (!emailLower) {
        onError?.('Email is required.');
        return;
      }
      try {
        setSending(true);
        await createTourInvite({
          ownerId,
          ownerName,
          ownerEmail,
          tourId,
          tourName,
          inviteeEmail: emailLower,
          inviteeName: name || '',
        });
        onSuccess?.(`Invitation sent to ${name || emailLower}.`);
        setSearch('');
        setSearchResults([]);
        setManualName('');
        setManualEmail('');
        setShowManual(false);
        onClose?.();
      } catch (err) {
        onError?.(err?.message || 'Could not send invite.');
      } finally {
        setSending(false);
      }
    },
    [ownerId, ownerName, ownerEmail, tourId, tourName, onSuccess, onError, onClose]
  );

  const confirmInvite = useCallback(
    (person) => {
      const label = memberDisplayName(person) || person.email;
      showConfirmation(
        'Send tour invitation?',
        `Invite ${label} to join this tour on Venue Report Card?`,
        () => sendInvite({ email: person.email, name: memberDisplayName(person) }),
        null,
        { confirmLabel: 'Send invite', confirmVariant: 'primary', iconName: 'paper-plane-outline' }
      );
    },
    [showConfirmation, sendInvite]
  );

  const confirmManualInvite = useCallback(() => {
    const email = normalizeEmail(manualEmail);
    if (!email) {
      onError?.('Email is required.');
      return;
    }
    if (invitedEmails.has(email)) {
      onError?.('That person is already on this tour.');
      return;
    }
    showConfirmation(
      'Send tour invitation?',
      `Invite ${manualName.trim() || email} to join this tour?`,
      () => sendInvite({ email, name: manualName.trim() }),
      null,
      { confirmLabel: 'Send invite', confirmVariant: 'primary', iconName: 'paper-plane-outline' }
    );
  }, [manualEmail, manualName, invitedEmails, onError, showConfirmation, sendInvite]);

  return (
    <ScrollView style={styles.detailScroll} showsVerticalScrollIndicator={false} contentContainerStyle={styles.detailScrollContent}>
      <View style={styles.detailHeader}>
        <Text style={[styles.detailName, { color: theme.text.primary }]}>Add tour member</Text>
        <TouchableOpacity
          onPress={onClose}
          hitSlop={8}
          accessibilityLabel="Close add member"
          style={[styles.detailCloseBtn, { borderColor: theme.ui.border }]}
        >
          <Ionicons name="close" size={14} color={theme.text.secondary} />
        </TouchableOpacity>
      </View>

      <Text style={[styles.detailHint, { color: theme.text.secondary }]}>
        Search for someone on VRC or invite by email. They&apos;ll get an in-app message and email to accept.
      </Text>

      <View style={[styles.searchField, { borderColor: theme.ui.border, backgroundColor: theme.background.secondary }]}>
        <Ionicons name="search-outline" size={16} color={theme.text.tertiary} />
        <TextInput
          style={[styles.searchInput, { color: theme.text.primary }]}
          value={search}
          onChangeText={runSearch}
          placeholder="Name, username, or email…"
          placeholderTextColor={theme.input.placeholder}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {searchLoading ? <ActivityIndicator size="small" color={theme.brand.accent} /> : null}
      </View>

      {search.trim().length >= 2 && !searchLoading && searchResults.length === 0 ? (
        <Text style={[styles.detailHint, { color: theme.text.tertiary }]}>No matching members found.</Text>
      ) : null}

      {searchResults.map((user) => (
        <TouchableOpacity
          key={user.id}
          style={[styles.searchResultRow, { borderColor: theme.ui.border, backgroundColor: theme.background.secondary }]}
          onPress={() => confirmInvite(user)}
          disabled={sending}
        >
          <TourPartyMemberAvatar member={{ id: user.id, name: memberDisplayName(user), photoURL: user.photoURL }} size={36} theme={theme} />
          <View style={styles.searchResultCopy}>
            <Text style={[styles.searchResultName, { color: theme.text.primary }]}>{memberDisplayName(user)}</Text>
            {user.username ? (
              <Text style={[styles.searchResultMeta, { color: theme.brand.accent }]}>@{user.username}</Text>
            ) : null}
            <Text style={[styles.searchResultMeta, { color: theme.text.secondary }]}>{user.email}</Text>
          </View>
          <Ionicons name="person-add-outline" size={18} color={theme.brand.primary} />
        </TouchableOpacity>
      ))}

      <TouchableOpacity style={styles.manualToggle} onPress={() => setShowManual((v) => !v)}>
        <Ionicons name={showManual ? 'chevron-up' : 'mail-outline'} size={14} color={theme.brand.accent} />
        <Text style={[styles.manualToggleText, { color: theme.brand.accent }]}>
          {showManual ? 'Hide email invite' : 'Invite someone not on VRC yet'}
        </Text>
      </TouchableOpacity>

      {showManual ? (
        <View style={styles.manualForm}>
          <TextInput
            style={[styles.manualInput, { borderColor: theme.ui.border, color: theme.text.primary }]}
            value={manualName}
            onChangeText={setManualName}
            placeholder="Name"
            placeholderTextColor={theme.input.placeholder}
          />
          <TextInput
            style={[styles.manualInput, { borderColor: theme.ui.border, color: theme.text.primary }]}
            value={manualEmail}
            onChangeText={setManualEmail}
            placeholder="Email (required)"
            placeholderTextColor={theme.input.placeholder}
            autoCapitalize="none"
            keyboardType="email-address"
          />
          <TouchableOpacity
            style={[styles.detailActionBtn, styles.detailActionBtnPrimary, { backgroundColor: theme.brand.primary, opacity: sending ? 0.6 : 1 }]}
            onPress={confirmManualInvite}
            disabled={sending}
          >
            {sending ? (
              <ActivityIndicator size="small" color={theme.button.primaryText} />
            ) : (
              <>
                <Ionicons name="paper-plane-outline" size={14} color={theme.button.primaryText} />
                <Text style={[styles.detailActionText, { color: theme.button.primaryText }]}>Send invitation</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      ) : null}
    </ScrollView>
  );
}

function buildFullAccessEntry() {
  const tabs = {};
  CALENDAR_PERMISSION_TABS.forEach((tabKey) => {
    tabs[tabKey] = PERMISSION_LEVELS.EDIT;
  });
  return {
    tabs,
    canManageParty: true,
    canManageShares: true,
  };
}

function TourPartyRailDetail({
  member,
  theme,
  isDarkMode,
  canManageParty,
  isOwner,
  ownerId,
  tourId,
  tourShareSettings,
  resendingId,
  permissionsSaving,
  onResend,
  onRemove,
  onPermissionsChange,
  onClose,
  interestItems,
}) {
  const navigation = useNavigation();
  const status = String(member.status || 'accepted').toLowerCase();
  const statusMeta = INVITE_STATUS_META[status] || INVITE_STATUS_META.pending;
  const profileAvailable = canOpenProfile(member);
  const isPending = status === 'pending';
  const isOwnerMember = isTourOwner(member.id, ownerId);
  const canRemove = canManageParty && member.inviteId && !isOwnerMember;
  const memberUid = String(member.id || '').trim();
  const canEditPermissions =
    isOwner &&
    !isOwnerMember &&
    member.onPlatform &&
    memberUid &&
    status === 'accepted';

  const permissionEntry = useMemo(() => {
    if (isOwnerMember) return buildFullAccessEntry();
    return getMemberPermissionsEntry(tourShareSettings || {}, memberUid, tourShareSettings?.managerIds);
  }, [isOwnerMember, tourShareSettings, memberUid]);

  const accessReadOnly = isOwner && !canEditPermissions;
  const accessReadOnlyMessage = isOwnerMember
    ? 'You created this tour and always have full access. These settings stay locked so you cannot lock yourself out.'
    : status !== 'accepted'
      ? 'Permissions can be configured after this member accepts their invitation.'
      : !member.onPlatform || !memberUid
        ? 'Permissions can be configured after this member joins on VRC with an account.'
        : '';

  const showAccessSection = isOwner;

  return (
    <ScrollView style={styles.detailScroll} showsVerticalScrollIndicator={false} contentContainerStyle={styles.detailScrollContent}>
      <View style={styles.detailHeader}>
        <TourPartyMemberAvatar member={member} size={48} theme={theme} />
        <View style={styles.detailHeaderCopy}>
          <Text style={[styles.detailName, { color: theme.text.primary }]} selectable>
            {member.name}
          </Text>
          {member.userTypeLabel ? (
            <Text style={[styles.detailRole, { color: member.userTypeColor || theme.text.secondary }]}>
              {member.userTypeLabel}
            </Text>
          ) : null}
        </View>
        <TouchableOpacity
          onPress={onClose}
          hitSlop={8}
          accessibilityLabel="Close member details"
          style={[styles.detailCloseBtn, { borderColor: theme.ui.border }]}
        >
          <Ionicons name="close" size={14} color={theme.text.secondary} />
        </TouchableOpacity>
      </View>

      {member.email ? (
        <Text style={[styles.detailLine, { color: theme.text.secondary }]} selectable>
          {member.email}
        </Text>
      ) : null}
      {member.username ? (
        <Text style={[styles.detailLine, { color: theme.brand.accent }]} selectable>
          @{member.username}
        </Text>
      ) : null}

      {interestItems.length > 0 ? (
        <View style={styles.detailInterests}>
          <CardInterestsDisplay
            items={interestItems}
            variant="inline"
            theme={theme}
            size="default"
          />
        </View>
      ) : null}

      <View style={styles.detailMeta}>
        {member.inviteId ? (
          <View style={[styles.detailPill, { borderColor: statusMeta.color }]}>
            <Ionicons name={statusMeta.icon} size={11} color={statusMeta.color} />
            <Text style={[styles.detailPillText, { color: statusMeta.color }]}>{statusMeta.label}</Text>
          </View>
        ) : member.role ? (
          <View style={[styles.detailPill, { borderColor: theme.brand.primary }]}>
            <Text style={[styles.detailPillText, { color: theme.brand.primary }]}>{member.role}</Text>
          </View>
        ) : null}
      </View>

      {showAccessSection ? (
        <View style={[styles.detailSection, { borderColor: theme.ui.border }]}>
          <Text style={[styles.detailSectionTitle, { color: theme.text.primary }]}>Tour access</Text>
          <Text style={[styles.detailHint, { color: theme.text.secondary }]}>
            {canEditPermissions
              ? 'Control what this member can view and edit on this tour.'
              : 'Review access for this tour member.'}
          </Text>
          <MemberPermissionsEditor
            entry={permissionEntry}
            theme={theme}
            isDarkMode={isDarkMode}
            saving={permissionsSaving}
            readOnly={accessReadOnly}
            readOnlyMessage={accessReadOnlyMessage}
            onChangeTab={(tabKey, level) =>
              onPermissionsChange?.(memberUid, {
                ...permissionEntry,
                tabs: { ...permissionEntry.tabs, [tabKey]: level },
              })
            }
            onToggleParty={(value) =>
              onPermissionsChange?.(memberUid, { ...permissionEntry, canManageParty: value })
            }
            onToggleShares={(value) =>
              onPermissionsChange?.(memberUid, { ...permissionEntry, canManageShares: value })
            }
          />
        </View>
      ) : null}

      <View style={styles.detailActions}>
        {profileAvailable ? (
          <TouchableOpacity
            style={[styles.detailActionBtn, { borderColor: theme.brand.primary }]}
            onPress={() => navigation.navigate('UserProfileScreen', { userId: member.id })}
          >
            <Ionicons name="person-outline" size={15} color={theme.brand.primary} />
            <Text style={[styles.detailActionText, { color: theme.brand.primary }]}>View profile</Text>
          </TouchableOpacity>
        ) : null}

        {canManageParty && isPending && member.inviteId ? (
          <TouchableOpacity
            style={[styles.detailActionBtn, { borderColor: theme.brand.accent }]}
            onPress={() => onResend?.(member)}
            disabled={resendingId === member.inviteId}
          >
            {resendingId === member.inviteId ? (
              <ActivityIndicator size="small" color={theme.brand.accent} />
            ) : (
              <>
                <Ionicons name="mail-outline" size={15} color={theme.brand.accent} />
                <Text style={[styles.detailActionText, { color: theme.brand.accent }]}>Resend invitation</Text>
              </>
            )}
          </TouchableOpacity>
        ) : null}

        {canRemove ? (
          <TouchableOpacity
            style={[styles.detailActionBtn, styles.detailActionBtnDanger, { borderColor: theme.status.error }]}
            onPress={() => onRemove?.(member)}
          >
            <Ionicons name="person-remove-outline" size={15} color={theme.status.error} />
            <Text style={[styles.detailActionText, { color: theme.status.error }]}>
              {isPending ? 'Revoke invitation' : 'Remove from tour'}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </ScrollView>
  );
}

/**
 * Narrow tour party strip beside the calendar. Cards + vertical interests collapse to ~108px;
 * selecting a member expands the drawer for details, permissions, and management.
 */
export function TourPartyRail({
  ownerId,
  ownerName,
  ownerEmail,
  ownerPhotoURL,
  tourId,
  tourName = '',
  currentUserId,
  tourShareSettings = null,
  theme,
  layout = 'vertical',
  visible = true,
  doorDateKey = null,
  onSuccess,
  onError,
}) {
  const db = getFirestore();
  const { isDarkMode } = useTheme();
  const { showConfirmation } = usePopup();
  const { members, loading } = useTourPartyMembers({
    ownerId,
    ownerName,
    ownerEmail,
    ownerPhotoURL,
    tourId,
  });
  const { interests: interestCatalog } = useInterestCatalog();
  const [expandedKey, setExpandedKey] = useState(null);
  const [panelMode, setPanelMode] = useState('member');
  const [resendingId, setResendingId] = useState(null);
  const [permissionsSaving, setPermissionsSaving] = useState(false);

  const isOwner = isTourOwner(currentUserId, ownerId);
  const canManageParty = canManageTourParty(currentUserId, ownerId, tourShareSettings);

  const invitedEmails = useMemo(() => {
    const set = new Set();
    members.forEach((m) => {
      const email = normalizeEmail(m.email);
      if (email) set.add(email);
    });
    if (ownerEmail) set.add(normalizeEmail(ownerEmail));
    return set;
  }, [members, ownerEmail]);

  const displayMembers = useMemo(() => {
    const active = members.filter(
      (m) => !['revoked', 'cancelled', 'declined'].includes(String(m.status || '').toLowerCase())
    );
    if (canManageParty) return active;
    const accepted = active.filter((m) => String(m.status || 'accepted') === 'accepted');
    return accepted.length > 0 ? accepted : active;
  }, [members, canManageParty]);

  const expandedMember = useMemo(
    () => displayMembers.find((m) => memberKey(m) === expandedKey) || null,
    [displayMembers, expandedKey]
  );

  const isExpanded = visible && (panelMode === 'add' || Boolean(expandedKey));
  const railWidth = visible
    ? isExpanded
      ? TOUR_PARTY_RAIL_WIDTH_EXPANDED
      : TOUR_PARTY_RAIL_WIDTH_COLLAPSED
    : 0;

  useEffect(() => {
    if (!visible) {
      setExpandedKey(null);
      setPanelMode('member');
    }
  }, [visible]);

  const closePanel = useCallback(() => {
    setExpandedKey(null);
    setPanelMode('member');
  }, []);

  const handleToggle = useCallback((key) => {
    setPanelMode('member');
    setExpandedKey((prev) => (prev === key ? null : key));
  }, []);

  const openAddPanel = useCallback(() => {
    setExpandedKey(null);
    setPanelMode('add');
  }, []);

  const handleResend = useCallback(
    async (member) => {
      if (!member.inviteId) return;
      try {
        setResendingId(member.inviteId);
        await resendTourInvite(member.inviteId, ownerId);
        onSuccess?.('Invitation email resent.');
      } catch (err) {
        onError?.(err?.message || 'Could not resend invite.');
      } finally {
        setResendingId(null);
      }
    },
    [ownerId, onSuccess, onError]
  );

  const handleRemove = useCallback(
    async (member) => {
      if (!member.inviteId) return;
      const isPending = String(member.status || 'pending').toLowerCase() === 'pending';
      try {
        await revokeTourInvite(member.inviteId, ownerId);
        onSuccess?.(isPending ? 'Invitation revoked.' : 'Removed from tour party.');
        closePanel();
      } catch (err) {
        onError?.(err?.message || 'Could not remove from tour party.');
      }
    },
    [ownerId, onSuccess, onError, closePanel]
  );

  const handlePermissionsChange = useCallback(
    async (memberUid, nextEntry) => {
      if (!isOwner || !tourId || !memberUid) return;
      if (memberUid === ownerId) {
        onError?.('Tour creator permissions cannot be changed.');
        return;
      }
      try {
        setPermissionsSaving(true);
        await saveMemberPermissionEntry(
          db,
          ownerId,
          tourId,
          memberUid,
          normalizeMemberPermissionsEntry(nextEntry),
          tourShareSettings || {}
        );
        onSuccess?.('Permissions updated.');
      } catch (err) {
        onError?.(err?.message || 'Could not save permissions.');
      } finally {
        setPermissionsSaving(false);
      }
    },
    [db, isOwner, tourId, ownerId, tourShareSettings, onSuccess, onError]
  );

  const confirmResend = useCallback(
    (member) => {
      showConfirmation(
        'Resend invitation?',
        `Send another invitation email to ${member.name || member.email}?`,
        () => handleResend(member),
        null,
        { confirmLabel: 'Resend email', confirmVariant: 'primary', iconName: 'mail-outline' }
      );
    },
    [showConfirmation, handleResend]
  );

  const confirmRemove = useCallback(
    (member) => {
      const isPending = String(member.status || 'pending').toLowerCase() === 'pending';
      showConfirmation(
        isPending ? 'Revoke invitation?' : 'Remove from tour?',
        isPending
          ? `${member.name || member.email} will no longer be able to accept this invitation.`
          : `Remove ${member.name || member.email} from this tour? They will lose access to tour content.`,
        () => handleRemove(member),
        null,
        {
          confirmLabel: isPending ? 'Revoke invite' : 'Remove member',
          confirmVariant: 'danger',
          iconName: isPending ? 'close-circle-outline' : 'person-remove-outline',
        }
      );
    },
    [showConfirmation, handleRemove]
  );

  const resolvedDoorKey = doorDateKey || '';
  const cardMotionItemCount = displayMembers.length + (canManageParty ? 1 : 0);
  const railVisible = Boolean(visible && tourId);
  const { phase: cardPhase, showCards, itemCountRef } = useDoorPartyCardMotion(
    resolvedDoorKey,
    railVisible
  );
  itemCountRef.current = cardMotionItemCount;

  if (!tourId) return null;

  if (loading && displayMembers.length === 0) {
    if (layout === 'horizontal') return null;
    if (!visible) return null;
    return (
      <View
        style={[
          styles.railShell,
          {
            width: TOUR_PARTY_RAIL_WIDTH_COLLAPSED,
            borderLeftColor: theme.ui.border,
            backgroundColor: theme.background.elevated,
          },
        ]}
      >
        <ActivityIndicator color={theme.brand.accent} style={{ marginTop: 24 }} />
      </View>
    );
  }

  if (!visible && layout === 'horizontal' && !showCards) return null;
  if (displayMembers.length === 0 && !canManageParty) return null;

  const renderAnimatedCard = (member, index) => {
    const key = memberKey(member);
    const interestItems = resolveInterestDisplayItems(member.interests || [], interestCatalog);
    const isOwnerMember = isTourOwner(member.id, ownerId);
    const canRemoveMember = canManageParty && member.inviteId && !isOwnerMember;
    return (
      <DoorPartyAnimatedCard key={key} index={index} phase={cardPhase} railVisible={railVisible}>
        <TourPartyRailCard
          member={member}
          theme={theme}
          selected={expandedKey === key && panelMode === 'member'}
          interestItems={interestItems}
          onPress={() => handleToggle(key)}
          canRemove={canRemoveMember}
          onRemove={() => confirmRemove(member)}
        />
      </DoorPartyAnimatedCard>
    );
  };

  const cardList = showCards
    ? displayMembers.map((member, index) => renderAnimatedCard(member, index))
    : null;

  const addCardButton =
    showCards && canManageParty ? (
      <DoorPartyAnimatedAddButton
        index={displayMembers.length}
        phase={cardPhase}
        railVisible={railVisible}
        theme={theme}
        onPress={openAddPanel}
      />
    ) : null;

  const detailPanel =
    panelMode === 'add' && canManageParty ? (
      <View style={[styles.detailPaneShell, { borderLeftColor: theme.ui.border }]}>
        <TourPartyRailAddPanel
          theme={theme}
          ownerId={ownerId}
          ownerName={ownerName}
          ownerEmail={ownerEmail}
          tourId={tourId}
          tourName={tourName}
          invitedEmails={invitedEmails}
          onSuccess={onSuccess}
          onError={onError}
          onClose={closePanel}
        />
      </View>
    ) : expandedMember ? (
      <View style={[styles.detailPaneShell, { borderLeftColor: theme.ui.border }]}>
        <TourPartyRailDetail
          member={expandedMember}
          theme={theme}
          isDarkMode={isDarkMode}
          canManageParty={canManageParty}
          isOwner={isOwner}
          ownerId={ownerId}
          tourId={tourId}
          tourShareSettings={tourShareSettings}
          resendingId={resendingId}
          permissionsSaving={permissionsSaving}
          onResend={confirmResend}
          onRemove={confirmRemove}
          onPermissionsChange={handlePermissionsChange}
          onClose={closePanel}
          interestItems={resolveInterestDisplayItems(expandedMember.interests || [], interestCatalog)}
        />
      </View>
    ) : null;

  const railTransitionStyle =
    Platform.OS === 'web'
      ? { transition: 'width 0.25s ease, border-left-width 0.25s ease, opacity 0.25s ease' }
      : {};

  if (layout === 'horizontal') {
    return (
      <View style={[styles.horizontalShell, { borderBottomColor: theme.ui.border, backgroundColor: theme.background.elevated }]}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.horizontalScroll}>
          {cardList}
          {addCardButton}
        </ScrollView>
        {detailPanel ? <View style={{ paddingHorizontal: 12, paddingBottom: 10 }}>{detailPanel}</View> : null}
      </View>
    );
  }

  return (
    <View
      style={[
        styles.railShell,
        {
          width: railWidth,
          maxWidth: railWidth,
          flexShrink: 0,
          borderLeftWidth: visible ? 1 : 0,
          borderLeftColor: theme.ui.border,
          backgroundColor: theme.background.elevated,
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
          ...railTransitionStyle,
        },
      ]}
    >
      <View style={[styles.railHeader, { borderBottomColor: theme.ui.border }]}>
        <Ionicons name="people-outline" size={13} color={theme.text.tertiary} />
        <Text style={[styles.railCount, { color: theme.text.secondary }]}>{displayMembers.length}</Text>
        {canManageParty ? (
          <TouchableOpacity onPress={openAddPanel} hitSlop={8} accessibilityLabel="Add tour member">
            <Ionicons name="add-circle-outline" size={16} color={theme.brand.primary} />
          </TouchableOpacity>
        ) : null}
      </View>

      <View style={[styles.railBody, styles.railBodyClip]}>
        <ScrollView
          style={{ width: TOUR_PARTY_RAIL_WIDTH_COLLAPSED, flexShrink: 0 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.railScroll}
        >
          {cardList}
          {addCardButton}
        </ScrollView>
        {detailPanel}
      </View>
    </View>
  );
}

const webInterestEmojiStyle = {
  fontSize: 12,
  lineHeight: '14px',
  display: 'block',
  textAlign: 'center',
};

const styles = StyleSheet.create({
  railShell: {
    flexShrink: 0,
    alignSelf: 'stretch',
    borderLeftWidth: 1,
    minWidth: 0,
    overflow: 'hidden',
    flexDirection: 'column',
  },
  railHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderBottomWidth: 1,
  },
  railCount: { fontSize: 11, fontWeight: '800' },
  railBody: { flex: 1, flexDirection: 'row', minHeight: 0 },
  railBodyClip: { overflow: 'hidden' },
  doorCardMotionWrap: {
    flexShrink: 0,
    ...(Platform.OS === 'web' ? { willChange: 'transform, opacity' } : {}),
  },
  railScroll: { paddingVertical: 8, paddingHorizontal: 6, gap: 8, alignItems: 'center' },
  railCard: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'visible',
    borderRadius: 10,
    flexShrink: 0,
  },
  railCardPressable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'hidden',
    borderRadius: 10,
  },
  railCardRemoveBtn: {
    position: 'absolute',
    top: -4,
    right: -4,
    zIndex: 5,
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 6px rgba(0,0,0,0.25)' }
      : {}),
  },
  railCardBody: {
    width: CARD_BODY_WIDTH,
    alignItems: 'center',
    gap: 3,
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  railCardName: { fontSize: 9, fontWeight: '800', textAlign: 'center', maxWidth: '100%' },
  railCardType: {
    fontSize: 7,
    fontWeight: '700',
    textAlign: 'center',
    maxWidth: '100%',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  interestStrip: {
    width: INTEREST_STRIP_WIDTH,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    paddingVertical: 6,
    paddingHorizontal: 2,
    borderLeftWidth: 1,
    backgroundColor: 'transparent',
  },
  interestEmoji: { fontSize: 12, lineHeight: 14, textAlign: 'center' },
  interestOverflow: { fontSize: 8, fontWeight: '800', lineHeight: 10, textAlign: 'center' },
  interestEmpty: { fontSize: 10, fontWeight: '600', lineHeight: 12 },
  addCardBtn: {
    width: CARD_WIDTH,
    height: CARD_WIDTH,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  detailPaneShell: {
    width: TOUR_PARTY_RAIL_DETAIL_WIDTH,
    maxWidth: TOUR_PARTY_RAIL_DETAIL_WIDTH,
    minWidth: TOUR_PARTY_RAIL_DETAIL_WIDTH,
    flexShrink: 0,
    borderLeftWidth: 1,
    minHeight: 0,
  },
  detailScroll: { flex: 1 },
  detailScrollContent: { paddingHorizontal: 14, paddingVertical: 12, gap: 10, paddingBottom: 20 },
  detailHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  detailHeaderCopy: { flex: 1, minWidth: 0, gap: 2 },
  detailName: { fontSize: 16, fontWeight: '800', lineHeight: 21 },
  detailRole: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  detailCloseBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  detailLine: { fontSize: 13, fontWeight: '600', lineHeight: 18 },
  detailHint: { fontSize: 12, lineHeight: 17 },
  detailInterests: { marginTop: 2, alignSelf: 'stretch' },
  detailMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  detailPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  detailPillText: { fontSize: 10, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.3 },
  detailSection: {
    marginTop: 4,
    paddingTop: 12,
    borderTopWidth: 1,
    gap: 8,
  },
  detailSectionTitle: { fontSize: 12, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.4 },
  detailActions: { marginTop: 6, gap: 8 },
  detailActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
  },
  detailActionBtnPrimary: { borderWidth: 0 },
  detailActionBtnDanger: { backgroundColor: 'rgba(231,76,60,0.08)' },
  detailActionText: { fontSize: 13, fontWeight: '700' },
  searchField: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  searchInput: { flex: 1, fontSize: 14, minWidth: 0, padding: 0 },
  searchResultRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 10,
    borderRadius: 10,
    borderWidth: 1,
  },
  searchResultCopy: { flex: 1, minWidth: 0, gap: 2 },
  searchResultName: { fontSize: 14, fontWeight: '700' },
  searchResultMeta: { fontSize: 12, fontWeight: '600' },
  manualToggle: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 4 },
  manualToggleText: { fontSize: 12, fontWeight: '700' },
  manualForm: { gap: 8 },
  manualInput: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 9,
    fontSize: 14,
  },
  horizontalShell: { borderBottomWidth: 1, paddingTop: 8, paddingBottom: 4, gap: 6 },
  horizontalScroll: { paddingHorizontal: 12, gap: 8, alignItems: 'flex-start' },
});

export default TourPartyRail;
